import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Sparkles, MessageCircle, Star as StarIcon, Settings as SettingsIcon,
  Images, ArrowRight, Image as ImageIcon, Video, Music, HardDrive, FolderOpen,
  Clock, ExternalLink
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CMS_DOC_META = {
  landing: {
    title: "Landing Hero",
    description: "4 section showcase di halaman utama \u2014 atelier, memory, koleksi, about.",
    icon: Sparkles,
    color: "#D4859B",
    to: "/admin/landing",
    site_link: "/",
  },
  about: {
    title: "About Page",
    description: "Hero, story sections, quote, dan tim.",
    icon: StarIcon,
    color: "#A8B85F",
    to: "/admin/about",
    site_link: "/about",
  },
  memory: {
    title: "Memory AI Curator",
    description: "Persona, prompt, model, dan threshold turn untuk AI chat.",
    icon: MessageCircle,
    color: "#8A6794",
    to: "/admin/memory",
    site_link: "/memory-bouquet",
  },
  settings: {
    title: "Site Settings",
    description: "Brand, tema, tipografi, audio, social, SEO.",
    icon: SettingsIcon,
    color: "#1C1A19",
    to: "/admin/settings",
    site_link: "/",
  },
};

function prettyDate(iso) {
  if (!iso) return "Belum pernah";
  try {
    const d = new Date(iso);
    return d.toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function prettyBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export default function AdminCMSOverview() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/cms/overview`, { withCredentials: true });
      setOverview(data);
    } catch (e) {
      toast.error("Gagal memuat CMS overview");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOverview(); }, []);

  if (loading) {
    return <Loader2 className="animate-spin text-seikat-pink mx-auto mt-20" size={28} />;
  }

  const docs = overview?.docs || [];
  const media = overview?.media || {};

  return (
    <div data-testid="admin-cms-overview" className="space-y-8">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="w-8 h-px bg-seikat-pink" />
            <span className="text-[10px] tracking-[0.35em] uppercase text-seikat-pink font-body">Content Studio</span>
          </div>
          <h1 className="font-heading text-4xl text-[#1C1A19] leading-tight">CMS <em className="text-seikat-pink">Overview</em></h1>
          <p className="font-body text-sm text-[#4A453F]/75 mt-1.5">Ringkasan semua konten dan media yang dikelola dari workspace ini.</p>
        </div>
        <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-4 py-2 border border-[#D4859B]/20 rounded-full text-xs font-body text-[#4A453F] hover:bg-[#FDF8F6]" data-testid="cms-view-site">
          <ExternalLink size={11} /> View Live Site
        </a>
      </div>

      {/* Document cards */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={13} className="text-seikat-pink" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#4A453F] font-body">Halaman & Konten</span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-4">
          {docs.map(d => {
            const meta = CMS_DOC_META[d.kind] || CMS_DOC_META.settings;
            const Icon = meta.icon;
            return (
              <div key={d.kind} className="group bg-white border border-[#D4859B]/15 rounded-2xl p-5 hover:border-seikat-pink hover:shadow-[0_8px_24px_-12px_rgba(212,133,155,0.35)] transition-all relative overflow-hidden" data-testid={`cms-card-${d.kind}`}>
                <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-[0.08]" style={{ backgroundColor: meta.color }} />
                <div className="relative">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${meta.color}14`, color: meta.color }}>
                      <Icon size={18} strokeWidth={1.6} />
                    </div>
                    <a href={meta.site_link} target="_blank" rel="noopener noreferrer" className="text-[10px] tracking-widest uppercase text-[#A8A29A] hover:text-seikat-pink flex items-center gap-1 mt-2">
                      Live <ExternalLink size={9} />
                    </a>
                  </div>
                  <h3 className="font-heading text-xl text-[#1C1A19] mb-1">{meta.title}</h3>
                  <p className="font-body text-xs text-[#4A453F]/80 leading-relaxed mb-3 min-h-[32px]">{meta.description}</p>

                  {/* doc stats */}
                  <div className="space-y-1 mb-4 min-h-[44px]">
                    {d.kind === "landing" && d.sections != null && (
                      <p className="text-[11px] text-[#4A453F] font-body"><span className="text-seikat-pink font-medium">{d.sections}</span> sections</p>
                    )}
                    {d.kind === "about" && (
                      <>
                        {d.sections != null && <p className="text-[11px] text-[#4A453F] font-body"><span className="text-seikat-pink font-medium">{d.sections}</span> story sections</p>}
                        {d.team_members != null && <p className="text-[11px] text-[#4A453F] font-body"><span className="text-seikat-pink font-medium">{d.team_members}</span> team members</p>}
                      </>
                    )}
                    {d.kind === "memory" && (
                      <>
                        {d.provider && <p className="text-[11px] text-[#4A453F] font-body">Provider <span className="text-seikat-pink font-medium">{d.provider}</span></p>}
                        {d.model && <p className="text-[11px] text-[#4A453F] font-body">Model <span className="text-seikat-pink font-medium">{d.model}</span></p>}
                      </>
                    )}
                    {d.kind === "settings" && d.brand && (
                      <p className="text-[11px] text-[#4A453F] font-body">Brand <span className="text-seikat-pink font-medium">{d.brand}</span></p>
                    )}
                  </div>

                  <p className="flex items-center gap-1 text-[10px] text-[#8A857D] font-body mb-3">
                    <Clock size={9} /> Diupdate {prettyDate(d.updated_at)}
                  </p>

                  <Link
                    to={meta.to}
                    data-testid={`cms-edit-${d.kind}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-seikat-pink text-white rounded-full text-xs font-body font-medium hover:bg-seikat-pink/90 transition-colors"
                  >
                    Edit <ArrowRight size={11} />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Media stats */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Images size={13} className="text-seikat-pink" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#4A453F] font-body">Media Library</span>
          </div>
          <Link to="/admin/media" className="text-xs text-seikat-pink hover:underline flex items-center gap-1" data-testid="cms-media-link">
            Kelola Media <ArrowRight size={10} />
          </Link>
        </div>
        <div className="bg-white border border-[#D4859B]/15 rounded-2xl p-5">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            {[
              { label: "Total File", value: media.total || 0, icon: HardDrive, color: "#1C1A19" },
              { label: "Gambar", value: media.images || 0, icon: ImageIcon, color: "#D4859B" },
              { label: "Video", value: media.videos || 0, icon: Video, color: "#8A6794" },
              { label: "Audio", value: media.audio || 0, icon: Music, color: "#A8B85F" },
              { label: "Folder", value: media.folders || 0, icon: FolderOpen, color: "#C19A6B" },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${s.color}14`, color: s.color }}>
                  <s.icon size={16} strokeWidth={1.6} />
                </div>
                <div>
                  <p className="font-body text-[10px] tracking-[0.2em] uppercase text-[#8A857D]">{s.label}</p>
                  <p className="font-heading text-2xl text-[#1C1A19] leading-none mt-0.5">{s.value}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-[#D4859B]/10 flex items-center justify-between flex-wrap gap-2">
            <p className="font-body text-xs text-[#4A453F]">Total ukuran library: <span className="font-medium text-seikat-pink">{prettyBytes(media.total_size_bytes)}</span></p>
            <Link to="/admin/media" className="px-4 py-1.5 bg-seikat-pink/10 hover:bg-seikat-pink/20 text-seikat-pink rounded-full text-xs font-body font-medium" data-testid="cms-go-media">
              Buka Media Library
            </Link>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={13} className="text-seikat-pink" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#4A453F] font-body">Aksi Cepat</span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: "Edit Landing", desc: "Sections, hero, media", to: "/admin/landing" },
            { label: "Edit About", desc: "Story & team", to: "/admin/about" },
            { label: "Tune Memory AI", desc: "Prompt + test chat", to: "/admin/memory" },
            { label: "Site Settings", desc: "Brand & SEO", to: "/admin/settings" },
          ].map(a => (
            <Link key={a.to} to={a.to} className="group bg-white border border-[#D4859B]/15 hover:border-seikat-pink rounded-2xl p-4 transition-colors">
              <p className="font-heading text-base text-[#1C1A19] group-hover:text-seikat-pink transition-colors">{a.label}</p>
              <p className="font-body text-[11px] text-[#8A857D] mt-1">{a.desc}</p>
              <ArrowRight size={12} className="text-[#A8A29A] group-hover:text-seikat-pink mt-2 transition-colors" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
